import React, { useState } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Button } from './ui/button';
import { Heart, LayoutDashboard, Calendar, CheckSquare, DollarSign, Users, UserCheck, LogOut, Menu, X, Radio, CircleDollarSign, Receipt } from 'lucide-react';
import { toast } from 'sonner';
import { AtmosphereStage } from './Atmosphere';

const navigation = [
  { name: 'Dashboard', href: '/dashboard', icon: LayoutDashboard, testId: 'nav-dashboard' },
  { name: 'Command Center', href: '/command-center', icon: Radio, testId: 'nav-command-center' },
  { name: 'Ceremonies', href: '/ceremonies', icon: Calendar, testId: 'nav-ceremonies' },
  { name: 'Tasks', href: '/tasks', icon: CheckSquare, testId: 'nav-tasks' },
  { name: 'Budget', href: '/budget', icon: DollarSign, testId: 'nav-budget' },
  { name: 'Vendors', href: '/vendors', icon: Users, testId: 'nav-vendors' },
  { name: 'Payments', href: '/payments', icon: CircleDollarSign, testId: 'nav-payments' },
  { name: 'Expenses', href: '/expenses', icon: Receipt, testId: 'nav-expenses' },
  { name: 'Guests', href: '/guests', icon: UserCheck, testId: 'nav-guests' },
];

export const DashboardLayout = () => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  const handleLogout = async () => {
    await logout();
    toast.success('Logged out successfully');
    navigate('/login');
  };

  return (
    <AtmosphereStage intensity="app">
      <div className="min-h-screen">
        <div className="hidden md:fixed md:inset-y-0 md:flex md:w-64 md:flex-col z-20">
          <div className="flex flex-col flex-grow border-r border-border bg-background overflow-y-auto">
            <div className="flex items-center flex-shrink-0 px-6 py-8 border-b border-border">
              <Heart className="h-8 w-8 text-primary" fill="currentColor" />
              <span className="ml-3 text-2xl font-serif font-semibold tracking-tight">Vivah</span>
            </div>
            <div className="mt-8 flex-grow flex flex-col">
              <nav className="flex-1 px-4 space-y-2" data-testid="sidebar-nav">
                {navigation.map((item) => {
                  const isActive = location.pathname === item.href;
                  return (
                    <Link
                      key={item.name}
                      to={item.href}
                      data-testid={item.testId}
                      className={`group flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-muted/80 hover:text-primary'
                      }`}
                    >
                      <item.icon className="mr-3 flex-shrink-0 h-5 w-5" />
                      {item.name}
                    </Link>
                  );
                })}
              </nav>
            </div>
            <div className="flex-shrink-0 flex border-t border-border p-4">
              <div className="flex-shrink-0 w-full">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{user?.name}</p>
                    <p className="text-xs text-muted-foreground truncate">{user?.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="icon"
                    onClick={handleLogout}
                    data-testid="logout-button"
                    className="ml-2"
                  >
                    <LogOut className="h-4 w-4" />
                  </Button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="md:pl-64 flex flex-col flex-1 relative z-10">
          <div className="sticky top-0 z-10 md:hidden pl-1 pt-1 sm:pl-3 sm:pt-3 bg-background border-b border-border">
            <button
              type="button"
              className="-ml-0.5 -mt-0.5 h-12 w-12 inline-flex items-center justify-center rounded-md text-foreground hover:text-primary focus:outline-none"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-button"
            >
              {mobileMenuOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>

          {mobileMenuOpen && (
            <div className="md:hidden fixed inset-0 z-40 bg-background">
              <div className="flex flex-col h-full">
                <div className="flex items-center justify-between px-6 py-8 border-b border-border">
                  <div className="flex items-center">
                    <Heart className="h-8 w-8 text-primary" fill="currentColor" />
                    <span className="ml-3 text-2xl font-serif font-semibold tracking-tight">Vivah</span>
                  </div>
                  <button onClick={() => setMobileMenuOpen(false)}>
                    <X className="h-6 w-6" />
                  </button>
                </div>
                <nav className="flex-1 px-4 py-4 space-y-2">
                  {navigation.map((item) => {
                    const isActive = location.pathname === item.href;
                    return (
                      <Link
                        key={item.name}
                        to={item.href}
                        onClick={() => setMobileMenuOpen(false)}
                        className={`group flex items-center px-4 py-3 text-sm font-medium rounded-md transition-colors ${
                          isActive
                            ? 'bg-primary text-primary-foreground'
                            : 'text-foreground hover:bg-muted/80 hover:text-primary'
                        }`}
                      >
                        <item.icon className="mr-3 flex-shrink-0 h-5 w-5" />
                        {item.name}
                      </Link>
                    );
                  })}
                </nav>
              </div>
            </div>
          )}

          <main className="flex-1">
            <div className="py-6 sm:py-8 px-4 sm:px-6 lg:px-8">
              <Outlet />
            </div>
          </main>
        </div>
      </div>
    </AtmosphereStage>
  );
};
